<template>
  <v-container>
    <!-- Filter Options -->
    <v-row>
      <v-col cols="12">
        <FilterBar surface="groupQc" :initial-filter="photoFilter" @apply-filters="setPhotoFilters($event)"></FilterBar>
      </v-col>
    </v-row>

    <v-progress-circular v-if="isLoading" indeterminate></v-progress-circular>

    <!-- No groups message -->
    <v-row v-if="!isLoading && qcGroups.length === 0">
      <v-col cols="12" class="text-center">
        <h3>No groups available for QC</h3>
      </v-col>
    </v-row>

    <!-- Groups List -->
    <div v-for="group in qcGroups" :key="group.id" class="group-card mb-6">
      <v-card>
        <v-card-title>
          Group: {{ group.response_group_id }} / Question: {{ group.question_id }}
          <v-chip v-if="group.needs_review" color="warning" class="ml-2" small>
            Needs Review
          </v-chip>
          <v-spacer></v-spacer>
          <span class="text-caption">Tagged by: {{ group.tagger || 'Unknown' }}</span>
        </v-card-title>

        <v-card-text>
          <!-- Horizontal Photo Gallery -->
          <div class="photo-gallery">
            <div
              v-for="photo in group.photos"
              :key="photo.id"
              class="photo-item"
            >
              <v-img
                :src="photo.image_url"
                :aspect-ratio="1"
                class="photo-image"
                contain
                @click="openLightbox(photo)"
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <div class="photo-id text-caption text-center">ID: {{ photo.response_id }}</div>
              <div
                v-if="photo.tagger"
                class="photo-tagger text-caption text-center"
              >
                Tagger: {{ photo.tagger }}
              </div>
              <!-- Read-only client context (FA question custom_data). Shown only
                   where the tenant opts in (MVP: Family Dollar). [IT-818] -->
              <div
                v-if="isPhotoNotesDisplayEnabled && photo.notes"
                class="photo-notes text-caption text-center"
              >
                {{ photo.notes }}
              </div>
            </div>
          </div>

          <!-- Current Tags -->
          <div class="mt-4">
            <strong>Current Tags:</strong>
            <v-chip-group column>
              <v-chip
                v-for="tag in getGroupTags(group)"
                :key="tag.id"
                small
                color="primary"
                outlined
              >
                {{ tagLabel(tag) }}
              </v-chip>
            </v-chip-group>
          </div>

          <!-- Tag Editor (expandable) -->
          <v-expansion-panels class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-header>
                Edit Tags
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="4">
                    <v-autocomplete
                      v-model="groupEdits[group.id].selectedCategory"
                      :items="allCats"
                      item-text="name"
                      item-value="id"
                      label="Category"
                    />
                  </v-col>
                  <v-col cols="8">
                    <v-autocomplete
                      v-model="groupEdits[group.id].selectedTags"
                      :items="getFilteredTags(groupEdits[group.id].selectedCategory)"
                      chips
                      item-text="name"
                      item-value="id"
                      label="Tags"
                      multiple
                      return-object
                    >
                      <template v-slot:selection="data">
                        <v-chip
                          v-bind="data.attrs"
                          :input-value="data.selected"
                          close
                          small
                          @click:close="removeTag(group.id, data.item)"
                        >
                          {{ data.item.name }}
                          <b v-if="data.item.category && data.item.category.id" style="font-size: 11px">
                            &nbsp;({{ data.item.category.name }})
                          </b>
                        </v-chip>
                      </template>
                      <template v-slot:item="data">
                        <v-list-item-content>
                          <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ data.item.category.name }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-checkbox
                      v-model="groupEdits[group.id].needsReview"
                      label="Needs Review"
                    ></v-checkbox>
                  </v-col>
                  <v-col class="text-right">
                    <v-btn color="primary" @click="saveGroupTags(group)">
                      Save Tags
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </div>

    <!-- Submit Buttons -->
    <v-container v-if="qcGroups.length > 0" class="fixed border--primary">
      <v-btn
        class="mr-2 float-right"
        color="success"
        @click="submitBatch({getNext: true})"
      >
        Mark Reviewed & Get Next Batch
      </v-btn>
      <v-btn
        class="mr-2 float-right"
        color="primary"
        @click="submitBatch({getNext: false})"
      >
        Mark Reviewed & Close
      </v-btn>
    </v-container>

    <!-- Lightbox Dialog -->
    <v-dialog v-model="lightboxOpen" max-width="90%">
      <v-card>
        <v-img
          v-if="lightboxPhoto"
          :src="lightboxPhoto.image_url"
          contain
          max-height="80vh"
        ></v-img>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="lightboxOpen = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import FilterBar from '@/components/filters/FilterBar.vue'
import {routeFilterSeed} from '@/components/filters/routeSeed'

export default {
  name: 'GroupQualityControl',
  components: {
    FilterBar,
  },
  data() {
    return {
      photoFilter: {
        fromDt: null,
        thruDt: null,
        tags: [],
        taggers: [],
        onlyNeedsReview: false,
        showQCed: false,
        retailer: null,
        rgidSearchFreeForm: null,
        respIdSearchFreeForm: null,
      },
      isLoading: false,
      lightboxOpen: false,
      lightboxPhoto: null,
      groupEdits: {}
    }
  },
  // Quick-link deep links (routeSeed.js) must seed the filter in created(),
  // BEFORE the filter child mounts: Vue 2 mounts the child before the parent,
  // so seeding in the parent's mounted() lands after the child has snapshotted
  // initialFilter — leaving the Applied-Filters chips blank and dropping the
  // scope on the next Apply. created() runs first, so the child snapshots the
  // seeded filter. Matches BoxTagging's timing.
  created() {
    this.photoFilter = {...this.photoFilter, ...routeFilterSeed(this.$route)}
  },
  mounted() {
    this.setQCGroups([])
    this.getGroups()
  },
  computed: {
    ...mapState({
      qcGroups: state => state.photoGroups.qcGroups,
      tags: state => state.tags.tags,
      categories: state => state.categories.categories,
    }),
    // IT-704 (P4): hide machine-owned (derived) categories/tags from the QC
    // editor once derivation is enabled.
    ...mapGetters(['visibleCategories', 'visibleTags', 'retainHumanTags', 'isPhotoNotesDisplayEnabled']),
    allCats() {
      var cats = this.visibleCategories.map(cat => ({ id: cat.id, name: cat.name }))
      cats.unshift({ id: 0, name: 'All' })
      return cats
    }
  },
  watch: {
    qcGroups: {
      handler(groups) {
        // Initialize edit state for each group
        groups.forEach(group => {
          if (!this.groupEdits[group.id]) {
            this.$set(this.groupEdits, group.id, {
              selectedCategory: 0,
              // IT-704 (P4): seed only human-picked tags when derivation is on,
              // so a QC resave omits the derived ones for the backend to
              // recompute (a no-op when off, so tags round-trip unchanged).
              selectedTags: [...this.retainHumanTags(group.tags)],
              needsReview: group.needs_review || false
            })
          }
        })
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['fetchQCGroups', 'bulkUpdateGroup', 'submitQCGroups']),
    ...mapMutations(['setMessage', 'setQCGroups']),

    setPhotoFilters(newFilter) {
      this.photoFilter = newFilter
      this.getGroups()
    },
    async getGroups() {
      this.isLoading = true
      let tagIds = this.photoFilter.tags.map(i => i.id)
      let taggerIds = this.photoFilter.taggers.map(i => i.id)
      // Pre-mapped here, like tags and taggers, rather than handed to the
      // store's id-coercion. P2.2 collapses this view's hand-built payload into
      // the shared builder; until then it keeps its own shape.
      let projectIds = (this.photoFilter.projects || []).map(i => i.id || i)
      let jobIds = (this.photoFilter.jobs || []).map(i => i.id || i)
      try {
        await this.fetchQCGroups({
          onlyNeedsReview: this.photoFilter.onlyNeedsReview,
          showQCed: this.photoFilter.showQCed,
          // IT-708: apply_group_filters honours this now — it widens the
          // correlated photo subquery to Photo.objects_all.
          includeInactive: this.photoFilter.includeInactive,
          rgidSearchFreeForm: this.photoFilter.rgidSearchFreeForm,
          respIdSearchFreeForm: this.photoFilter.respIdSearchFreeForm,
          question_id: this.photoFilter.question_id,
          // IT-703: the store maps fromDt/thruDt to the backend's
          // fromDateVal/toDateVal. Sending the backend's names from here meant
          // `payload.fromDt` was undefined and the dates never left the
          // browser — the same collected-chipped-dropped shape as groupId, on
          // the same page. Fixed here rather than in the store, which is
          // correct for its other caller (GroupCountQC passes filter names).
          fromDt: this.photoFilter.fromDt,
          thruDt: this.photoFilter.thruDt,
          tags: tagIds,
          taggers: taggerIds,
          retailer: this.photoFilter.retailer,
          // IT-707 P2.4: apply_group_filters has read all four since IT-528 and
          // GroupCountQC already sends them to this same endpoint — this view
          // was simply the one that never offered them.
          projects: projectIds,
          jobs: jobIds,
          department: this.photoFilter.department,
          country: this.photoFilter.country,
        })
      } finally {
        this.isLoading = false
      }
    },

    tagLabel(tag) {
      // IT-703: same ambiguity as the Edit Tags selector — Family Dollar has
      // tags named 1-5 in two categories, so bare names render as "1, 1" and a
      // reviewer cannot tell an OOS score from a Cleanliness one. Caught in the
      // browser: the selector was fixed while these chips still showed "1, 1".
      const category = tag && tag.category && tag.category.name
      return category ? `${tag.name} (${category})` : `${tag.name}`
    },

    getGroupTags(group) {
      return group.tags || []
    },

    getFilteredTags(categoryId) {
      let categId = parseInt(categoryId)
      if (categId > 0) {
        return this.visibleTags.filter(tag => tag.category.id === categId)
      }
      return this.visibleTags
    },

    removeTag(groupId, tag) {
      const edit = this.groupEdits[groupId]
      const index = edit.selectedTags.findIndex(t => t.id === tag.id)
      if (index >= 0) {
        edit.selectedTags.splice(index, 1)
      }
    },

    async saveGroupTags(group) {
      const edit = this.groupEdits[group.id]
      const selectedTagIds = edit.selectedTags.map(tag => tag.id)

      const payload = {
        group_id: group.id,
        selected_tags: selectedTagIds,
        needs_review: edit.needsReview,
        isQc: true
      }

      try {
        await this.bulkUpdateGroup(payload)
        this.setMessage({
          text: 'Tags Updated Successfully',
          type: 'success',
          alert: true
        })
      } catch (e) {
        this.setMessage({
          text: 'Error updating tags',
          type: 'error',
          alert: true
        })
      }
    },

    async submitBatch(payload) {
      await this.submitQCGroups()

      this.setMessage({
        text: 'Groups Marked Reviewed',
        type: 'success',
        alert: true
      })

      this.groupEdits = {}

      if (payload.getNext) {
        this.getGroups()
      }
    },

    openLightbox(photo) {
      window.open(photo.image_url, '_blank')
    }
  }
}
</script>

<style scoped>
.border--primary {
  border: 1px solid #666;
  border-radius: 4px;
  padding: 10px;
}

.fixed {
  position: fixed !important;
  bottom: 0 !important;
  z-index: 999;
  background: white;
}

.group-card {
  margin-bottom: 24px;
}

.photo-gallery {
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 16px 0;
  overflow-x: auto;
  min-height: 150px;
}

.photo-item {
  flex: 0 0 auto;
  width: 150px;
}

.photo-image {
  cursor: pointer;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.photo-image:hover {
  border-color: var(--v-primary-base);
}

.photo-id {
  margin-top: 4px;
  color: #666;
}

.photo-notes {
  margin-top: 0;
  color: #333;
  font-weight: 500;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
}
</style>
